import classNames from 'classnames';
import FJBackIcon from '../../assets/back.svg?react';
import FJScroller from '../../common/components/FJScroller/FJScroller';
import FJSkeletonScreen from '@src/common/components/FJSkeletonScreen/FJSkeletonScreen';
import FJImage from '@src/common/components/FJImage/FJImage';
import FJLocalStore from '../../store/FJLocalStore';
import { type FJDetailsListItem } from '../../redux/action/FJDetailsPageAction';
import { GENERATE_STATE } from '../../constants/FJEnumConstant';
import styles from './FJDetailsSidebar.module.less';

/** 侧栏序号：按列表渲染顺序，与旧版 `01` 样式一致 */
// function ordinalBadge(ordinal: number): string {
//     return String(ordinal).padStart(2, '0');
// }

function itemThumbUrl(item: FJDetailsListItem): string {
    if ('videoData' in item) {
        return item.videoData.thumbnailUrl;
    }
    return '';
}

/** 封面尚未上传完成：侧栏缩略图 FJImage 显示 loading */
function isThumbnailCoverLoading(item: FJDetailsListItem): boolean {
    if (item.status === GENERATE_STATE.PROCESSING || item.status === GENERATE_STATE.PENDING) {
        return false;
    }
    if (!('videoData' in item)) {
        return false;
    }
    const { thumbnailUrl, thumbnailUrlStatus } = item.videoData;
    if (thumbnailUrlStatus === GENERATE_STATE.PROCESSING) {
        return true;
    }
    if (thumbnailUrlStatus === GENERATE_STATE.SUCCEEDED) {
        return false;
    }
    return !String(thumbnailUrl ?? '').trim();
}

interface FJDetailsSidebarProps {
    items: FJDetailsListItem[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    onBack: () => void;
    /** 侧栏「返回」下方的项目主标题 */
    projectTitle?: string;
}

const SIDEBAR_GENERATING_SKELETON_LAYOUT = [
    [
        { width: 56, height: 56 },
        { width: 168, height: 16 },
        { width: 100, height: 16 },
    ],
];

export default function FJDetailsSidebar({ items, selectedId, onSelect, onBack, projectTitle }: FJDetailsSidebarProps) {
    return (
        <aside className={styles.root}>
            <button type='button' className={styles.back_btn} onClick={onBack}>
                <FJBackIcon className={styles.back_icon} aria-hidden />
                <span>{FJLocalStore._('back-to-home')}</span>
            </button>

            {projectTitle ? <div className={styles.project_title}>{projectTitle}</div> : null}

            <FJScroller className={styles.nav} role='navigation' axis='y'>
                {items.map((item, index) => {
                    const isActive = selectedId === item.id;
                    const commonProps = {
                        key: item.id,
                        type: 'button' as const,
                        className: classNames(styles.nav_item, isActive && styles.nav_item_active),
                        onClick: () => onSelect(item.id),
                    };

                    if (item.status === GENERATE_STATE.PROCESSING || item.status === GENERATE_STATE.PENDING) {
                        return (
                            <button {...commonProps}>
                                <FJSkeletonScreen
                                    list={SIDEBAR_GENERATING_SKELETON_LAYOUT}
                                    titleClassName={styles.nav_skeleton_title}
                                    ulClassName={styles.nav_skeleton_ul}
                                    innerClassName={styles.nav_skeleton_inner}
                                    itemClassName={styles.nav_skeleton_item}
                                />
                            </button>
                        );
                    }

                    const thumbSrc = itemThumbUrl(item);
                    const blurUrl = thumbSrc?.trim();
                    const thumbCoverLoading = isThumbnailCoverLoading(item);
                    return (
                        <button {...commonProps}>
                            <div className={styles.thumb_wrap}>
                                {blurUrl ? (
                                    <div
                                        className={styles.thumb_blur_bg}
                                        style={{ backgroundImage: `url(${blurUrl})` }}
                                        aria-hidden
                                    />
                                ) : null}
                                <span className={styles.badge} aria-hidden>
                                    {index + 1}
                                </span>
                                <FJImage
                                    imgSrc={thumbSrc}
                                    isLoading={thumbCoverLoading}
                                    boxClassName={styles.nav_thumb_box}
                                    imgClassName={styles.nav_thumb}
                                    disableDefaultImgClass
                                    alt=''
                                />
                            </div>
                            <span className={styles.nav_title}>{item.title}</span>
                        </button>
                    );
                })}
            </FJScroller>
        </aside>
    );
}
